'use client'

import * as React from 'react'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import { tw } from 'utils/classnames'
import { useIsLoggedInQuery } from 'auth/hooks'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'
import { PaymentStatus } from '@repo/graphql-types'
import type { CoinPack } from '@repo/graphql-types'
import { Z_INDEX_MODALS } from 'constants/modals'

import { getPaymentRecoveryModelFromPaymentStatus } from '../components/payment-recovery-dialog/normalizers'
import { PaymentRecoveryStatusDialog } from '../components/payment-recovery-dialog/payment-recovery-status-dialog'
import type { TopUpReturn } from './hooks/use-coins-top-up'
import { useCoinsTopUp } from './hooks/use-coins-top-up'

interface CoinsTopUpButtonProps {
  style?: React.CSSProperties
  label: string | React.ReactNode
  className?: string
  onSuccess?: () => void
  coinPack: CoinPack
}

// oxlint-disable-next-line complexity
export function CoinsTopUpButton({
  label,
  className,
  onSuccess,
  style = {},
  coinPack,
}: CoinsTopUpButtonProps) {
  const isLoggedInQuery = useIsLoggedInQuery()
  const [variant, setVariant] = React.useState<TopUpReturn | null>(null)

  const { buyCoins, isPending } = useCoinsTopUp()

  const handleBuyCoinPacksClick = React.useCallback(
    async (coinPack: CoinPack) => {
      sendAmplitudeEvent(AmplitudeEvents.COIN_PACK_PURCHASE_CLICKED, {
        coin_pack: coinPack,
      })

      const response = await buyCoins({ coinPack })

      if (!response) {
        return
      }

      if (response.paymentsStatus === PaymentStatus.Succeeded) {
        onSuccess?.()
      }

      setVariant(response)
    },
    [buyCoins, onSuccess],
  )

  if (!isLoggedInQuery.data) {
    return null
  }

  const closeModal = () => {
    setVariant(null)
  }

  const recoveryModel = getPaymentRecoveryModelFromPaymentStatus({
    paymentStatus: variant?.paymentsStatus,
    message: variant?.message,
    error: variant?.error,
  })

  return (
    <>
      <button
        style={style}
        onClick={() => handleBuyCoinPacksClick(coinPack)}
        disabled={isPending}
        className={tw('relative flex items-center justify-center', className)}
      >
        <div
          className={isPending ? 'invisible' : 'visible'}
          {...(typeof label === 'string' ? { dangerouslySetInnerHTML: { __html: label } } : {})}
        >
          {typeof label !== 'string' ? label : null}
        </div>
        {isPending ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <AnimatedSpinnerIcon />
          </span>
        ) : null}
      </button>

      <PaymentRecoveryStatusDialog
        model={recoveryModel}
        onClose={closeModal}
        zIndex={Z_INDEX_MODALS.oneClickUpgrade.modal}
        overlayZIndex={Z_INDEX_MODALS.oneClickUpgrade.modal - 1}
      />
    </>
  )
}
